import React, { lazy, Suspense, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Routes, Route, Navigate } from 'react-router';

import * as Sentry from '@sentry/react';

import App from './modules/App/App';

import authorization from './components/Auth/AuthorizationComponent';
// import pageAnimator from './components/Content/PageAnimator';
import withRouter from './components/Router/WithRouter';

import Loader from './components/Loader/Loader';
import CmsPage from './modules/Cms/pages/view/CmsPage';

// Role Filter
const root = ['root'];

const admin = ['admin'];
const adminUp = ['root', 'admin'];

const manager = ['manager'];
const managerUp = ['manager', 'admin', 'root'];

const professor = ['professor'];
const professorUp = ['professor', 'manager', 'admin', 'root'];

const redactor = ['redactor'];
const redactorUp = ['redactor', 'professor', 'manager', 'admin', 'root'];

const proctor = ['proctor'];
const proctorUp = ['proctor', 'professor', 'manager', 'admin', 'root'];

const corrector = ['corrector'];
const correctorUp = ['corrector', 'professor', 'manager', 'admin', 'root'];

const sessionAuth = ['proctor', 'corrector', 'professor', 'manager', 'admin', 'root'];

const student = ['student'];
const studentUp = ['student', 'corrector', 'proctor', 'redactor', 'professor', 'manager', 'admin', 'root'];

const baseRoute = '/';

// Public
const HomePage = lazy(() => import('./modules/App/pages/view/HomePage'));
const ErrorPage = lazy(() => import('./modules/Error/pages/ErrorPage'));

// Applicant
const ApplicantList = lazy(() => import('./modules/Applicant/pages/ApplicantListPage'));
const ApplicantView = lazy(() => import('./modules/Applicant/pages/ApplicantViewPage'));
const SessionPass = lazy(() => import('./modules/Session/pages/SessionPassPage'));

// Console
const InstitutionList = lazy(() => import('./modules/Institution/pages/InstitutionListPage'));
const InstitutionEdit = lazy(() => import('./modules/Institution/pages/InstitutionEditPage'));
const SubjectDashboard = lazy(() => import('./modules/Subject/pages/SubjectDashboard'));
const SubjectCentrale = lazy(() => import('./modules/Subject/pages/SubjectCentralePage'));
const SubjectVariableEdit = lazy(() => import('./modules/Subject/pages/SubjectVariablesPage'));
const SubjectPreview = lazy(() => import('./modules/Subject/pages/SubjectPreviewPage'));
const SubjectStats = lazy(() => import('./modules/Subject/pages/SubjectStatsPage'));
const QuestionEdit = lazy(() => import('./modules/Question/pages/QuestionEditPage'));
const SubjectEdit = lazy(() => import('./modules/Subject/pages/SubjectEditPage'));
const SubjectsList = lazy(() => import('./modules/Subject/pages/SubjectListPage'));
const ThemesList = lazy(() => import('./modules/Theme/pages/ThemeListPage'));
const ThemesEdit = lazy(() => import('./modules/Theme/pages/ThemeEditPage'));
const SessionDashboard = lazy(() => import('./modules/Session/pages/SessionDashboard'));
const SessionEdit = lazy(() => import('./modules/Session/pages/SessionEditPage'));
const ApplicantEdit = lazy(() => import('./modules/Applicant/pages/ApplicantEditPage'));

const SessionConvocation = lazy(() => import('./modules/Session/pages/SessionConvocationPage'));
const SessionProctor = lazy(() => import('./modules/Session/pages/SessionProctorPage'));
const SessionCorrection = lazy(() => import('./modules/Session/pages/SessionCorrectionPage'));
const SessionStats = lazy(() => import('./modules/Session/pages/SessionStatsPage'));
const SessionList = lazy(() => import('./modules/Session/pages/SessionListPage'));
const SessionLTISelect = lazy(() => import('./modules/Session/pages/SessionLTISelectPage'));
const UserGroupsList = lazy(() => import('./modules/UserGroup/pages/UserGroupsListPage'));
const UserGroupEdit = lazy(() => import('./modules/UserGroup/pages/UserGroupEditPage'));
const UserDashboard = lazy(() => import('./modules/User/pages/UserDashboardPage'));
const UserList = lazy(() => import('./modules/User/pages/UserListPage/UserListPage'));
const UserStudentSearch = lazy(() => import('./modules/User/pages/UserStudentSearchPage/UserStudentSearchPage'));
const UserHistory = lazy(() => import('./modules/User/pages/UserHistoryPage/UserHistoryPage'));
const UserEdit = lazy(() => import('./modules/User/pages/UserEditPage/UserEditPage'));
const InvoiceDashboard = lazy(() => import('./modules/Invoice/pages/InvoiceDashboardPage'));
const InvoiceList = lazy(() => import('./modules/Invoice/pages/InvoiceListPage'));
const InvoiceEdit = lazy(() => import('./modules/Invoice/pages/InvoiceEditPage'));
const InvoiceView = lazy(() => import('./modules/Invoice/pages/InvoiceViewPage'));
const Config = lazy(() => import('./modules/Config/pages/ConfigPage/ConfigPage'));
const CmsList = lazy(() => import('./modules/Cms/pages/list/CmsListPage'));
const CmsEdit = lazy(() => import('./modules/Cms/pages/edit/CmsEditPage'));
const RecognitionTest = lazy(() => import('./modules/Video/pages/RecognitionTestPage'));

const UserEnvironment = lazy(() => import('./modules/User/pages/UserEnvironmentPage/UserEnvironmentPage'));
const UserProfile = lazy(() => import('./modules/User/pages/UserProfilePage/UserProfilePage'));
const UserLogin = lazy(() => import('./modules/User/pages/UserLoginPage/UserLoginPage'));
const Contact = lazy(() => import('./modules/Contact/pages/ContactPage/ContactPage'));

function getPath(path = '', isConsole = false, isLocalized = false) {
	return `${baseRoute}${isLocalized ? ':lang/' : ''}${isConsole ? 'console' : ''}${path && isConsole && !isLocalized ? '/' : ''}${path || ''}`;
}

const setAuth = (route, auth = student) => ({ auth, ...route, children: (route.children || []).map(childRoute => setAuth(childRoute, auth)) });

export const routesData = []
// Console routes
	.concat([
		{
			path: getPath('institutions', true),
			component: InstitutionList,
			auth: managerUp,
		},
		{
			path: getPath('institution', true),
			component: InstitutionEdit,
			auth: managerUp,
			children: [
				{
					path: ':_id',
					component: InstitutionEdit,
					auth: managerUp,
				},
			],
		},
		{
			path: getPath('subjects', true),
			component: SubjectsList,
			auth: redactorUp,
			children: [
				{
					path: 'dashboard',
					component: SubjectDashboard,
					auth: redactorUp,
				},
				{
					path: 'centrale',
					component: SubjectCentrale,
					auth: redactorUp,
				},
			],
		},
		{
			path: getPath('subject', true),
			component: SubjectEdit,
			auth: redactorUp,
			children: [
				{
					path: ':_id',
					component: SubjectEdit,
					auth: redactorUp,
					children: [
						{
							path: 'variables',
							component: SubjectVariableEdit,
							auth: redactorUp,
						},
						{
							path: 'questions',
							component: QuestionEdit,
							auth: redactorUp,
						},
						{
							path: 'preview',
							component: SubjectPreview,
							auth: redactorUp,
						},
						{
							path: 'stats',
							component: SubjectStats,
							auth: redactorUp,
						},
					],
				},
			],
		},
		{
			path: getPath('themes', true),
			component: ThemesList,
			auth: redactorUp,
		},
		{
			path: getPath('theme', true),
			component: ThemesEdit,
			auth: redactorUp,
			children: [
				{
					path: ':_id',
					component: ThemesEdit,
					auth: redactorUp,
				},
			],
		},

		{
			path: getPath('sessions', true),
			component: SessionList,
			auth: sessionAuth,
			children: [
				{
					path: 'dashboard',
					component: SessionDashboard,
					auth: sessionAuth,
				},
				{
					path: 'proctor',
					component: SessionProctor,
					auth: proctorUp,
				},
				{
					path: ':status',
					component: SessionList,
					auth: sessionAuth,
				},
				{
					path: 'lti/select',
					component: SessionLTISelect,
					auth: sessionAuth,
				},
			],
		},
		{
			path: getPath('session', true),
			component: SessionEdit,
			auth: professorUp,
			children: [
				{
					path: ':_id',
					component: SessionEdit,
					auth: professorUp,
					children: [
						{
							path: 'applicants',
							component: ApplicantEdit,
							auth: professorUp,
						},
						{
							path: 'convocation',
							component: SessionConvocation,
							auth: professorUp,
						},
						{
							path: 'proctor',
							component: SessionProctor,
							auth: proctorUp,
						},
						{
							path: 'correction',
							component: SessionCorrection,
							auth: correctorUp,
							children: [
								{
									path: ':panel',
									component: SessionCorrection,
									auth: correctorUp,
									children: [
										{
											path: ':applicant',
											component: SessionCorrection,
											auth: correctorUp,
										},
									],
								},
							],
						},
						{
							path: 'stats',
							component: SessionStats,
							auth: correctorUp,
						},
					],
				},
			],
		},
		{
			path: getPath('users/groups', true),
			component: UserGroupsList,
			auth: professorUp,
		},
		{
			path: getPath('users/group', true),
			component: UserGroupEdit,
			auth: professorUp,
			children: [
				{
					path: ':_id',
					component: UserGroupEdit,
					auth: professorUp,
				},
			],
		},
		{
			path: getPath('users/dashboard', true),
			component: UserDashboard,
			auth: professorUp,
		},
		{
			path: getPath('users', true),
			component: UserList,
			auth: managerUp,
			children: [
				{
					path: 'students',
					component: UserStudentSearch,
					auth: professorUp,
				},
			],
		},
		{
			path: getPath('user', true),
			component: UserEdit,
			auth: managerUp,
			children: [
				{
					path: ':_id',
					component: UserEdit,
					auth: managerUp,
					children: [
						{
							path: 'history',
							component: UserHistory,
							auth: professorUp,
						},
					],
				},
			],
		},
		{
			path: getPath('invoices', true),
			component: InvoiceList,
			auth: adminUp,
			children: [
				{
					path: ':type',
					component: InvoiceList,
					auth: adminUp,
				},
				{
					path: 'dashboard',
					component: InvoiceDashboard,
					auth: adminUp,
				},
			],
		},
		{
			path: getPath('invoice', true),
			component: InvoiceEdit,
			auth: adminUp,
			children: [
				{
					path: 'add/:type',
					component: InvoiceEdit,
					auth: root,
				},
				{
					path: ':id',
					component: InvoiceEdit,
					auth: adminUp,
					children: [
						{
							path: 'view',
							component: InvoiceView,
							auth: adminUp,
						},
					],
				},
			],
		},
		{
			path: getPath('contents', true),
			component: CmsList,
		},
		{
			path: getPath('content', true),
			component: CmsEdit,
			children: [
				{
					path: ':id',
					component: CmsEdit,
				},
			],
		},
		{
			path: getPath('config', true),
			component: Config,
			auth: root,
		},
		{
			path: getPath('video/recognition', true),
			component: RecognitionTest,
			auth: root,
		},
	])

// Applicant routes
	.concat([
		{
			path: getPath('sessions'),
			component: ApplicantList,
		},
		{
			path: getPath('session/:code'),
			component: SessionPass,
			children: [
				{
					path: 'pass',
					component: SessionPass,
				},
				{
					path: 'view',
					component: ApplicantView,
				},
			],
		},
		{
			path: getPath('user/testenvironment'),
			component: UserEnvironment,
		},
		{
			path: getPath('user/profile'),
			component: UserProfile,
		},
	]).map(route => setAuth(route, studentUp))

// Public routes
	.concat([
		{
			path: getPath('page/contact'),
			component: Contact,
		},
		{
			path: getPath('user/login'),
			component: UserLogin,
		},
		{
			path: getPath('login'),
			component: UserLogin,
		},
		{
			path: getPath(),
			component: HomePage,
		},
	]).concat([
		{
			path: getPath('*', false, true),
			component: CmsPage,
		},
		{
			path: getPath(':slug', false, true),
			component: CmsPage,
		},
	]);;

// react-router setup with code-splitting
// More info: http://blog.mxstbr.com/2016/01/react-apps-with-pages/
// const oldRoutes = (
//     <Route path="/" component={App}>
//
//         <Route
//             path="institution/simulator"
//             getComponent={(nextState, cb) => {
//                 require.ensure([], require => {
//                     cb(null, require('./modules/Institution/pages/CreditSimulatorPage').default);
//                 });
//             }}
//             onEnter={onEnter}
//         />
//
//
//         {routes.map(route =>
//             <Route
//                 key={route.path}
//                 path={route.path}
//                 getComponent={(nextState, cb) => {
//                     require.ensure([], require => {
//                         cb(null, route.component);
//                     });
//                 }}
//                 onEnter={onEnter}
//             />
//         )}
//     </Route>
// );

const renderRoutes = (routes, prefix = '') => {
	return routes.map((route, index) => {
		const path = `${prefix ? `${prefix}/` : ''}${route.path}`;
		const key = route.path || index;

		if(route.redirect) {
			return <Route key={key} path={path} element={<Navigate replace to={route.redirect} />} />;
		}
		// if(route.pagePath) {
		//     return (
		//         <Route key={index} {...route} render={props => <AsyncComponent path={route.pagePath} {...props} />} />
		//     );
		// }
		const Component = withRouter(route.auth ? authorization(route.auth)(route.component) : route.component);
		return (
			<Route key={key}>
				{route.children && route.children.length > 0 && renderRoutes(route.children, path)}
				<Route path={path} index={(route.children || []).length} element={<Component />} />
			</Route>
		);
	});
};

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

// react-router setup with code-splitting
function AppRoutes({ ssr = false }) {
	return (
		<Suspense fallback={<Loader hideSpinner />}>
			<SentryRoutes>
				<Route path="/" element={<App />}>
					{renderRoutes(routesData)}
				</Route>
			</SentryRoutes>
		</Suspense>
	);
}

AppRoutes.propTypes = {
	ssr: PropTypes.bool,
};

export default AppRoutes;
